// BudgetPanel.js
import React, { useState } from 'react';
import { ChevronLeft } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import { Bars, FeedButton, FeedCard, FeedSection, FeedSegments, PanelState } from '../feed/FeedCard';
import {
  fetchBudgetFunctions,
  formatDay,
  formatDollars,
  formatPercent,
  listTitle,
  rankedLists,
} from '../../utils/spending';

/**
 * BudgetPanel
 *
 * What the government is spending on: every agency's obligations by budget
 * function, from /spending/functions. The opening panel, because it answers
 * the question most readers arrive with, and the only one that sees what the
 * award data cannot -- interest on the debt, federal pay, trust-fund
 * benefits.
 *
 * Three cuts of one total, each adding up to it exactly: by purpose (budget
 * function), by kind of spending (object class) and by agency. Kind of
 * spending gets the second bar color, because it is a different division of
 * the same money.
 *
 * A purpose opens in place to its subfunctions, agencies and largest
 * accounts. The drill-down's lists are found rather than named (see
 * rankedLists), since each level answers with its own keys.
 *
 * ─── Opening a purpose ────────────────────────────────────────────────────────
 *
 * A purpose is asked for by its budget function code (570 is Medicare). The
 * code is read from whichever field the row carries it in, since the rows'
 * key for it is not fixed by this file: `code` first, then the other names a
 * function code goes by. A row with no code is not offered as a tap -- a tap
 * that could only fetch the whole budget again would look like one that
 * loaded nothing.
 *
 * The breakdown sits inside the answer's `function` object -- its
 * subfunctions, agencies and accounts -- while the answer's top level repeats
 * the whole budget (`functions`, the totals, the period). So the lists are
 * read from `function`, and the top-level `functions` list is never shown
 * here: under a purpose's name it would read as that purpose's breakdown,
 * which is what made a tap look like it loaded nothing. If `function` holds
 * no lists, the card says the breakdown is not available rather than
 * repeating the totals.
 *
 * The subtitle always says how far the year runs and whether it is over:
 * the current year arrives a month at a time, weeks after each month closes.
 */

const CUTS = [
  { id: 'functions', tone: 'gov' },
  { id: 'by_object_class', tone: 'kind' },
  { id: 'by_agency', tone: 'gov' },
];

// Where a budget function row may carry its code.
const CODE_FIELDS = ['code', 'function_code', 'budget_function_code', 'function', 'id'];

const functionCodeOf = (row) => {
  for (const field of CODE_FIELDS) {
    const value = row?.[field];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
};

// Lists that belong to the top level and never to a purpose's breakdown.
const TOP_LEVEL_ONLY = new Set(['functions']);

function BudgetPanel() {
  const { t } = useTranslation('common');
  const [cut, setCut] = useState('functions');
  const [drill, setDrill] = useState(null);

  const budget = useApi2Resource(() => fetchBudgetFunctions(), []);
  const detail = useApi2Resource(drill ? () => fetchBudgetFunctions(drill.code) : null, [
    drill ? drill.code : null,
  ]);

  const data = budget.data;

  const subtitle = data
    ? `${t('common:obligatedThrough', '{{amount}} obligated by every agency through {{date}}', {
        amount: formatDollars(data.total_obligations),
        date: formatDay(data.through) || data.through,
      })}${
        data.complete_year
          ? t('common:fullYear', ' (the full year).')
          : t('common:yearNotOver', ', the latest month published. The year is not over.')
      }`
    : null;

  const cutConfig = CUTS.find((option) => option.id === cut) || CUTS[0];
  const rows = Array.isArray(data?.[cut]) ? data[cut] : [];

  // Purposes open only when their rows carry a code. If none do, the hint
  // that invites a tap is left out too.
  const canDrill = cut === 'functions' && rows.some((row) => functionCodeOf(row));

  const openPurpose = (row) => {
    const code = functionCodeOf(row);
    if (!code) return;
    setDrill({ code, name: row.name });
  };

  const cutLabel = (id) =>
    ({
      functions: t('common:byPurpose', 'By purpose'),
      by_object_class: t('common:byKind', 'By kind'),
      by_agency: t('common:byAgency', 'By agency'),
    })[id];

  const renderTop = () => (
    <FeedCard
      footer={
        <p className="spending-card-note">
          {t(
            'common:budgetGrossNote',
            'Gross obligations: a payment from one federal account to another appears in both, so this runs larger than the outlays the Treasury reports.'
          )}
        </p>
      }
    >
      <FeedSegments
        className="spending-card-tabs"
        label={t('common:budgetCut', 'How to divide it')}
        value={cut}
        onChange={setCut}
        options={CUTS.map((option) => ({ id: option.id, label: cutLabel(option.id) }))}
      />

      <PanelState
        loading={budget.loading}
        error={budget.error}
        empty={!budget.loading && !budget.error && !rows.length}
      />

      {rows.length > 0 && (
        <>
          {canDrill && (
            <p className="spending-hint">
              {t('common:tapPurpose', 'Tap a purpose to see the agencies and accounts inside it.')}
            </p>
          )}
          <Bars
            rows={rows}
            tone={cutConfig.tone}
            format={(value) => formatDollars(value)}
            sub={(row) => formatPercent(row.share)}
            onSelect={canDrill ? openPurpose : undefined}
          />
        </>
      )}
    </FeedCard>
  );

  const renderDrill = () => {
    // The purpose's own lists live in `function`; an older answer that put
    // them at the top level is still read, minus the whole-budget list.
    const purpose = detail.data?.function;
    const source =
      purpose && typeof purpose === 'object' && !Array.isArray(purpose) ? purpose : detail.data;
    const lists = rankedLists(source).filter(({ key }) => !TOP_LEVEL_ONLY.has(key));
    const loaded = !detail.loading && !detail.error;

    return (
      <FeedCard
        title={drill.name}
        footer={
          <FeedButton primary onClick={() => setDrill(null)}>
            <ChevronLeft size={12} className="me-1" aria-hidden />
            {t('common:allPurposes', 'All purposes')}
          </FeedButton>
        }
      >
        <PanelState
          loading={detail.loading}
          error={detail.error}
          empty={loaded && !lists.length}
          emptyText={t('common:purposeBreakdownUnavailable', 'The breakdown for this purpose is not available yet.')}
        />

        {loaded &&
          lists.map(({ key, rows: listRows }) => (
            <div key={key} className="spending-list">
              <h3 className="spending-list-title">{listTitle(key, t)}</h3>
              <Bars
                rows={listRows}
                tone={key === 'by_object_class' ? 'kind' : 'gov'}
                format={(value) => formatDollars(value)}
                sub={(row) =>
                  typeof row.share === 'number'
                    ? t('common:shareOfPurpose', '{{share}} of this purpose', {
                        share: formatPercent(row.share),
                      })
                    : null
                }
              />
            </div>
          ))}
      </FeedCard>
    );
  };

  return (
    <FeedSection
      id="budget"
      title={
        data?.fiscal_year
          ? t('common:budgetTitleYear', 'What the government is spending on, FY{{year}}', {
              year: data.fiscal_year,
            })
          : t('common:budgetTitle', 'What the government is spending on')
      }
      subtitle={subtitle}
    >
      {drill ? renderDrill() : renderTop()}
    </FeedSection>
  );
}

export default BudgetPanel;